import { useQuery } from '@tanstack/react-query';
import { gatewayAuthClient } from '@terra/shared-rest-clients-gateway-auth';
import type {
  GetUserTenantsResponse,
  UserTenant,
} from '@terra/shared-contracts-gateway-auth';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';

const QUERY_KEY = 'query.user-tenants';

const fetchUserTenants = async (): Promise<GetUserTenantsResponse> => {
  const authHeaders = await getAuthHeaders();
  const response = await gatewayAuthClient.getTenants({
    query: { fresh: 'true', torchSupportedOnly: 'true' },
    overrideClientOptions: {
      baseUrl: getCurrentGatewayUrl(),
    },
    extraHeaders: authHeaders,
  });

  if (response.status !== 200) {
    throw new Error(`Failed to fetch tenants: ${response.status}`);
  }

  return response.body;
};

export const useUserTenants = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { data, isLoading, error, refetch, isFetched } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: fetchUserTenants,
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    tenants: data?.tenants ?? ([] as UserTenant[]),
    currentTenantId: data?.currentTenantId,
    isTerraUser: data?.isTerraUser ?? false,
    hasAccessToTerraTenant: data?.hasAccessToTerraTenant ?? false,
    isLoading,
    error,
    refetch,
    isFetched,
  };
};
